import React, { FC, useState, useEffect, ReactFragment } from 'react';
import { Dialog, Typography, Checkbox, Button, styled, useMediaQuery } from '@material-ui/core';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight } from 'src/config/api/models/FontAwesome';
import theme from 'src/config/theme';
import { StyledCheckboxFormControlLabel } from 'src/modules/shared/components';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import VideoPlayer from 'src/modules/trajectory/components/TrajectoryDetail/blocks/VideoPlayer/VideoPlayer';
import { useUser } from 'src/modules/shared';

export type ModalProps = {
  title: string;
  hasSeen?: () => void;
  body: (string | ReactFragment)[];
  videoUrl: string;
  checkboxLabel: string;
  buttonText: string;
  log?: LogActionTypes;
  logTeachersLobby?: LogActionTypes;
  downloadUrl?: string;
  setFullScreen?: () => void;
};

type DialogProps = {
  showDialog: boolean;
  onClose: () => void;
  props: ModalProps;
};

const DialogWrapper = styled(Dialog)({
  maxWidth: '479px',
  margin: 'auto',
  '& .MuiDialog-paper': {
    margin: '0px',
  },
});

const Title = styled('div')({
  display: 'flex',
  gap: '50px',
  justifyContent: 'space-between',
});

const DialogContentWrapper = styled('div')({
  padding: '10px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

const VideoWrapper = styled('div')({});

const Content = styled('div')({
  width: '100%',
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'justify',
});

const DemoDialog: FC<DialogProps> = ({ showDialog, onClose, props }) => {
  const log = useDataLogging();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
  const { isStudent, isTeacher } = useUser();

  const handleClose = () => {
    setIsChecked(false);
    onClose();
  };

  const handleButton = () => {
    if (isChecked && props.hasSeen) {
      props.hasSeen();
    }
    if (props.downloadUrl) {
      if (props.logTeachersLobby) {
        log(props.logTeachersLobby);
      }
      window.open(props.downloadUrl, '_blank');
    }
    if (props.setFullScreen) {
      props.setFullScreen();
    }
    handleClose();
  };

  useEffect(() => {
    if (!props.log) return;
    if (!showDialog) return;
    if (!isTeacher) return;
    return log(props.log);
  }, [log, props, showDialog, isTeacher]);

  if (isStudent) {
    return null;
  }

  return (
    <DialogWrapper
      fullScreen={isMobile}
      open={showDialog}
      onClose={() => handleClose()}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogContentWrapper>
        <Title>
          <Typography variant="h3">{props.title}</Typography>
          <FontAwesomeSpriteIcon
            style={{ cursor: 'pointer' }}
            color={theme.config.appbarColors.icon}
            iconSize="lg"
            icon={SpriteIconsLight.XMARK}
            onClick={() => handleClose()}
          />
        </Title>
        <VideoWrapper>
          <VideoPlayer height="245px" url={props.videoUrl} />
        </VideoWrapper>
        <Content>
          {props.body.map((item, index) => {
            return (
              <Typography key={index} variant="body2">
                {item}
              </Typography>
            );
          })}
        </Content>
        <>
          <StyledCheckboxFormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                onChange={() => setIsChecked(!isChecked)}
                color="primary"
              />
            }
            labelPlacement="end"
            label={props.checkboxLabel}
          />
        </>
        <>
          {props.buttonText && (
            <Button
              style={{ height: '36px', width: 'fit-content' }}
              size="small"
              color="primary"
              variant="contained"
              type="submit"
              onClick={() => handleButton()}
              disableElevation={true}
            >
              {props.buttonText}
            </Button>
          )}
        </>
      </DialogContentWrapper>
    </DialogWrapper>
  );
};

export default DemoDialog;
